import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '@nyce/admin-kit/api';
import { FormDialog, NumberField, Progress, TabPanel } from '@nyce/admin-kit/console';
import { Button, MultiSelect, Select } from '@nyce/admin-kit/ui';
import { db, type DbServerRef } from '../../api/db';
import {
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  Tabs,
  useAsync,
  useInterval,
  useNotice,
} from '../../components/common';
import { formatCount, formatDateTime } from '../../lib/format';
import { serverName, useSortedServers } from '../../pages/databases/shared';
import {
  indexAdvisor,
  isReviewActive,
  isScanActive,
  canAdvise,
  type IndexAdvisorSettings,
  type Scan,
} from '../api/indexAdvisor';
import { advisorErrorText, RunChip, useParam, type Notify } from '../shared';
import { CandidatesTab } from './CandidatesTab';
import { ScanDatabasesTab } from './ScanDatabasesTab';
import { DefaultSetTab } from './DefaultSetTab';
import { RecommendationTab } from './RecommendationTab';
import '../indexadvisor.css';

type Tab = 'candidates' | 'databases' | 'default' | 'recommendation';

const TABS: { value: Tab; label: string }[] = [
  { value: 'candidates', label: 'Candidates' },
  { value: 'databases', label: 'Databases' },
  { value: 'default', label: 'Default set' },
  { value: 'recommendation', label: 'Recommendation' },
];

const TABS_ID = 'iax-tabs';
const POLL_MS = 3000;

/** A scan by id, or null when there is none (`latest` before the first succeeded scan). */
async function loadScan(id: number | 'latest') {
  try {
    return await indexAdvisor.scan(id);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

/**
 * Databases → Index advisor: fleet scans of the advisor's recommendations across tenant databases, counted per
 * normalized index and checked against the default set; a model's review per candidate; the resulting
 * `index-additions.sql` blocks. The hub reads only; it never creates an index.
 */
export function IndexAdvisorPage({ scopes }: { scopes: readonly string[] }) {
  const [tabParam, setTab] = useParam<Tab>('tab', 'candidates');
  const tab = TABS.some((t) => t.value === tabParam) ? tabParam : 'candidates';
  const [scanParam, setScanParam] = useParam('scan', '');
  const selected: number | 'latest' = Number(scanParam) > 0 ? Number(scanParam) : 'latest';
  const advise = canAdvise(scopes);
  const notice = useNotice();
  const settings = useAsync(() => indexAdvisor.settings(), []);
  const serverList = useAsync(() => db.servers(), []);
  const servers = useSortedServers(serverList.data);
  const detail = useAsync(() => loadScan(selected), [selected]);
  const scan = detail.data?.scan ?? null;
  // what the lists depend on: they reload when the scan or its review moves on
  const stateKey = scan ? `${scan.id}/${scan.status}/${scan.review.status}` : '';
  const scans = useAsync(() => indexAdvisor.scans(20), [stateKey]);
  useInterval(() => void detail.reload(), POLL_MS, isScanActive(scan) || isReviewActive(scan));

  const onScan = (s: Scan) => {
    if (s.id === scan?.id) detail.setData((d) => (d ? { ...d, scan: s } : d));
    void detail.reload();
  };

  return (
    <Page
      title="Index advisor"
      description="Indexes the database advisor recommends, counted across tenant databases and checked against the default set (platform template + index additions). Read-only: the hub proposes scripts, it never creates an index."
      actions={
        <Link to="/databases?tab=advice" className="nyk-ui-btn nyk-ui-btn-ghost nyk-ui-btn-sm">
          One database
        </Link>
      }
    >
      {notice.element}
      <Tabs id={TABS_ID} value={tab} onChange={setTab} options={TABS} aria-label="Index advisor" />
      <TabPanel tabs={TABS_ID} value={tab} className="iax-tab-body">
        {tab !== 'default' && (
          <ScanBar
            scans={scans.data ?? []}
            scan={scan}
            selected={scanParam}
            onSelect={setScanParam}
            loading={detail.loading && !detail.data}
            error={detail.error}
            advise={advise}
            settings={settings.data}
            servers={servers}
            onStarted={(s) => {
              notice.success(`Scan #${s.id} started`);
              setScanParam(String(s.id));
              void scans.reload();
            }}
            onChanged={onScan}
            notice={notice}
          />
        )}
        {tab === 'candidates' && scan && <CandidatesTab scan={scan} stateKey={stateKey} />}
        {tab === 'databases' && scan && (
          <ScanDatabasesTab databases={detail.data?.databases ?? []} servers={servers} />
        )}
        {tab === 'recommendation' && scan && <RecommendationTab scan={scan} stateKey={stateKey} />}
        {tab === 'default' && (
          <DefaultSetTab
            advise={advise}
            settings={settings.data}
            settingsError={settings.error}
            onSaved={(s) => settings.setData(s)}
            servers={servers}
            notice={notice}
          />
        )}
      </TabPanel>
    </Page>
  );
}

function ScanBar({
  scans,
  scan,
  selected,
  onSelect,
  loading,
  error,
  advise,
  settings,
  servers,
  onStarted,
  onChanged,
  notice,
}: {
  scans: Scan[];
  scan: Scan | null;
  selected: string;
  onSelect: (id: string) => void;
  loading: boolean;
  error: string | null;
  advise: boolean;
  settings: IndexAdvisorSettings | null;
  servers: DbServerRef[];
  onStarted: (s: Scan) => void;
  onChanged: (s: Scan) => void;
  notice: Notify;
}) {
  const [targets, setTargets] = useState<string[]>([]);
  const [reviewing, setReviewing] = useState(false);
  const busy = scans.some(isScanActive) || isScanActive(scan);
  const options = [
    { value: '', label: 'Latest succeeded scan' },
    ...scans.map((s) => ({
      value: String(s.id),
      label: `#${s.id} · ${s.status} · ${formatDateTime(s.createdAt)}`,
    })),
  ];
  if (selected && !scans.some((s) => String(s.id) === selected))
    options.push({ value: selected, label: `#${selected}` });
  const start = async () => {
    try {
      onStarted(await indexAdvisor.startScan(targets));
      setTargets([]);
    } catch (e) {
      notice.error(advisorErrorText(e));
    }
  };
  const cancel = async () => {
    if (!scan) return;
    try {
      onChanged(await indexAdvisor.cancelScan(scan.id));
      notice.success(`Cancelling scan #${scan.id}`);
    } catch (e) {
      notice.error(advisorErrorText(e));
    }
  };
  const model = settings?.review.model ?? '';
  const reviewBlocked = !settings
    ? 'Loading settings…'
    : !settings.review.configured
      ? 'The review is not configured on this hub (IndexAdvisor:Llm).'
      : !scan || scan.status !== 'succeeded'
        ? 'Only a succeeded scan can be reviewed.'
        : isReviewActive(scan)
          ? 'A review of this scan is in progress.'
          : null;
  const r = scan?.review;
  return (
    <section className="nyk-card iax-scan-bar" aria-label="Scan" data-testid="iax-scan-bar">
      <div className="iax-scan-facts">
        <Select aria-label="Scan" value={selected} onChange={onSelect} options={options} />
        {scan && (
          <>
            <RunChip status={scan.status} />
            <span>
              Scan <b>#{scan.id}</b> by {scan.createdBy}, {formatDateTime(scan.createdAt)}
            </span>
            <span className="iax-muted">
              {scan.serverIds.length
                ? scan.serverIds.map((id) => serverName(servers, id)).join(', ')
                : 'all servers'}
            </span>
          </>
        )}
      </div>
      <ErrorBox error={error} />
      <Loading loading={loading} />
      {!loading && !error && !scan && (
        <EmptyState
          title={selected ? 'No such scan.' : 'No scan has finished yet.'}
          hint={advise ? 'Start one below.' : 'An operator with ophub:db:advise starts scans.'}
        />
      )}
      {scan && (
        <div className="iax-scan-facts">
          {isScanActive(scan) ? (
            <Progress value={scan.databasesDone} max={scan.databasesTotal} />
          ) : (
            <span>{formatCount(scan.databasesTotal)} databases</span>
          )}
          <span>{formatCount(scan.tenants)} tenants</span>
          <span>{formatCount(scan.withAdvisor)} with the advisor</span>
          {scan.cancelRequested && isScanActive(scan) && <span className="iax-warn">cancelling…</span>}
          {scan.error && <span className="nyk-text-red nyk-wrap">{scan.error}</span>}
        </div>
      )}
      {r && r.status !== 'none' && (
        <div className="iax-scan-facts" data-testid="iax-review-status">
          <span>Review</span>
          <RunChip status={r.status} />
          {r.model && <span className="nyk-mono">{r.model}</span>}
          {r.spec?.minDatabases != null && (
            <span className="iax-muted">
              ≥ {r.spec.minDatabases} databases, ≤ {r.spec.maxTables} tables
            </span>
          )}
          {r.usage && (
            <span className="iax-muted">
              {formatCount(r.usage.inputTokens)} in / {formatCount(r.usage.outputTokens)} out tokens,{' '}
              {formatCount(r.usage.tables)} tables
            </span>
          )}
          {r.by && (
            <span className="iax-muted">
              by {r.by}
              {r.finishedAt ? `, ${formatDateTime(r.finishedAt)}` : ''}
            </span>
          )}
          {r.error && <span className="nyk-text-red nyk-wrap">{r.error}</span>}
        </div>
      )}
      {advise && (
        <div className="iax-scan-actions">
          <MultiSelect
            label="Servers"
            placeholder="All servers"
            value={targets}
            onChange={setTargets}
            options={servers.map((s) => ({ value: s.id, label: s.name || s.id }))}
          />
          <ConfirmButton
            size="sm"
            disabled={busy}
            title={busy ? 'A scan is already queued or running.' : undefined}
            message={`Start an index scan over ${
              targets.length ? targets.map((id) => serverName(servers, id)).join(', ') : 'all servers'
            }? It reads the advisor of every tenant database, read-only.`}
            confirmLabel="Start scan"
            onConfirm={() => void start()}
          >
            Start scan
          </ConfirmButton>
          {scan && isScanActive(scan) && (
            <ConfirmButton
              size="sm"
              variant="ghost"
              message={`Cancel scan #${scan.id}? The databases read so far are kept.`}
              confirmLabel="Cancel scan"
              onConfirm={() => void cancel()}
            >
              Cancel scan
            </ConfirmButton>
          )}
          <Button
            size="sm"
            variant="secondary"
            disabled={!!reviewBlocked}
            title={reviewBlocked ?? undefined}
            onClick={() => setReviewing(true)}
          >
            Review…
          </Button>
          {reviewBlocked && settings && <span className="nyk-ui-hint">{reviewBlocked}</span>}
        </div>
      )}
      {reviewing && scan && (
        <ReviewDialog
          scan={scan}
          model={model}
          onClose={() => setReviewing(false)}
          onQueued={(s) => {
            setReviewing(false);
            onChanged(s);
            notice.success(`Review of scan #${s.id} queued`);
          }}
        />
      )}
    </section>
  );
}

/** Asks the configured model to judge the scan's candidates, per table. */
function ReviewDialog({
  scan,
  model,
  onClose,
  onQueued,
}: {
  scan: Scan;
  model: string;
  onClose: () => void;
  onQueued: (s: Scan) => void;
}) {
  const [minDatabases, setMin] = useState<number | null>(scan.review.spec?.minDatabases ?? 2);
  const [maxTables, setMax] = useState<number | null>(scan.review.spec?.maxTables ?? 40);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const valid = !!minDatabases && minDatabases >= 1 && !!maxTables && maxTables >= 1 && maxTables <= 200;
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      onQueued(await indexAdvisor.review(scan.id, { minDatabases: minDatabases!, maxTables: maxTables! }));
    } catch (e) {
      setError(advisorErrorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <FormDialog
      title={`Review scan #${scan.id}`}
      size="sm"
      onClose={onClose}
      onSubmit={() => void submit()}
      submitLabel="Start review"
      canSubmit={valid}
      busy={busy}
      error={error}
      draft={{ minDatabases, maxTables }}
      formClassName="iax-form"
    >
      <p className="nyk-ui-hint">
        {model ? <span className="nyk-mono">{model}</span> : 'The configured model'} judges each candidate
        recommended by at least this many databases, one table at a time, against its statements, the table's
        write rate and existing indexes. Each table is one model call; the verdicts are stored with the scan
        {scan.review.status === 'succeeded' || scan.review.status === 'failed'
          ? ' and replace the previous review'
          : ''}
        .
      </p>
      <div className="iax-form-row">
        <NumberField label="Min databases" value={minDatabases} onChange={setMin} min={1} integer required />
        <NumberField
          label="Max tables"
          value={maxTables}
          onChange={setMax}
          min={1}
          max={200}
          integer
          required
        />
      </div>
    </FormDialog>
  );
}
